/**
 * Per-screen "Uitleg" (help) content, ported 1:1 from the Claude Design project.
 * The side panel reads its content from here, keyed by the current screen.
 *
 * Content strings are sourced from i18next (namespace: "guide") — call
 * getGuide(t) with the `t` function from useTranslation() to build the
 * Record<string, GuideEntry> shape that consumers expect.
 */
import type { TFunction } from "i18next";

export interface GuideEntry {
  title: string;
  purpose: string;
  can: string[];
  steps: string[];
  tip: string;
}

const GUIDE_KEYS = [
  "dashboard",
  "converteren",
  "functionaliteiten",
  "results",
  "result",
  "users",
  "companies",
  "roles",
  "configuration",
] as const;

const CAN_COUNT: Record<(typeof GUIDE_KEYS)[number], number> = {
  dashboard: 3,
  converteren: 3,
  functionaliteiten: 3,
  results: 3,
  result: 3,
  users: 3,
  companies: 3,
  roles: 3,
  configuration: 3,
};

const STEPS_COUNT: Record<(typeof GUIDE_KEYS)[number], number> = {
  dashboard: 3,
  converteren: 3,
  functionaliteiten: 3,
  results: 3,
  result: 3,
  users: 3,
  companies: 3,
  roles: 3,
  configuration: 3,
};

export function getGuide(t: TFunction): Record<string, GuideEntry> {
  const guide: Record<string, GuideEntry> = {};
  for (const key of GUIDE_KEYS) {
    const canCount = CAN_COUNT[key];
    const stepsCount = STEPS_COUNT[key];
    guide[key] = {
      title: t(`guide.${key}.title`),
      purpose: t(`guide.${key}.purpose`),
      can: Array.from({ length: canCount }, (_, i) => t(`guide.${key}.can${i}`)),
      steps: Array.from({ length: stepsCount }, (_, i) => t(`guide.${key}.steps${i}`)),
      tip: t(`guide.${key}.tip`),
    };
  }
  return guide;
}

/** Map a route pathname to a guide key (result-detail routes share the 'result' guide). */
export function guideKeyForPath(pathname: string): string {
  const p = pathname.toLowerCase();
  if (p.startsWith("/dashboard")) return "dashboard";
  if (p.startsWith("/converteren")) return "converteren";
  if (p.startsWith("/functionaliteiten")) return "functionaliteiten";
  if (p.startsWith("/results")) return "results";
  if (p.startsWith("/users")) return "users";
  if (p.startsWith("/companies")) return "companies";
  if (p.startsWith("/configuration")) return "configuration";
  if (p.startsWith("/roles")) return "roles";
  // Result / detail pages
  if (
    p.startsWith("/conversion-results") ||
    p.startsWith("/document-analysis") ||
    p.startsWith("/specs-comparison") ||
    p.startsWith("/company-standard-results") ||
    p.startsWith("/document-contradiction")
  )
    return "result";
  if (p.startsWith("/request-specs") || p.startsWith("/ai-insights")) return "functionaliteiten";
  return "dashboard";
}
